import * as React from 'react';

import {ErrorState} from 'shells/error-state/error-state';

import type {
    SmsEditionFragment as Edition,
    SmsDripEditionFragment as DripEdition,
} from 'nutshell-graphql-types';

import {EditionSendingState} from '../../editions/engagement/edition-sending-state';
import {updateSearchWithNewFilter} from '../../utils';

import {SMS_EDITION_FILTER_KEYS} from '../consts';

import {useGetSmsEditionEngagementStats} from './graphql/hooks';

import {EngagementBar} from '.';

type Props = {
    edition: Edition | DripEdition;
};

export function EditionEngagement(props: Props) {
    const {smsEdition, error, isLoading} = useGetSmsEditionEngagementStats(
        props.edition.id,
        getPollTimeout(props.edition)
    );

    const editionStatus = (smsEdition && smsEdition.status) || props.edition.status;

    if (error) {
        return <ErrorState errorMessage={error} />;
    }

    // If sending and less than 1% of messages have been delivered...
    if (!isDripEdition(props.edition) && shouldShowSendingState(props.edition)) {
        return <EditionSendingState edition={props.edition} />;
    }

    return (
        <EngagementBar
            engagementStats={
                (smsEdition && smsEdition.engagementStats) || props.edition.engagementStats
            }
            isLoading={isLoading}
            onClickStat={
                editionStatus === 'SENT'
                    ? (filterKey: string) => {
                          const newSearch = updateSearchWithNewFilter(
                              filterKey,
                              props.edition.id,
                              location.search,
                              // @ts-expect-error
                              SMS_EDITION_FILTER_KEYS
                          );

                          // This is the relative path needed for the Link to work
                          return `recipients?${newSearch}`;
                      }
                    : undefined
            }
        />
    );
}

function getPollTimeout(edition: Edition | DripEdition) {
    return edition.status === 'SENDING' && edition.campaign.type !== 'DRIP_SEQUENCE'
        ? 5000
        : undefined;
}

function shouldShowSendingState(edition: Edition) {
    if (edition.status !== 'SENDING' || edition.campaign.type === 'DRIP_SEQUENCE') {
        return false;
    }

    if (!edition.audience) {
        return true;
    }

    let count;
    if (edition.audience.fromSavedFilter && edition.audience.fromSavedFilter.count) {
        count = edition.audience.fromSavedFilter.count.value;
    } else if (edition.audience.membershipStats && edition.audience.membershipStats.totalCount) {
        count = edition.audience.membershipStats.totalCount.value;
    } else {
        return false;
    }

    // If sending and less than 1% of messages have been delivered...
    if (edition.engagementStats.totalSentCount < count / 100) {
        return true;
    }

    return false;
}

function isDripEdition(edition: Edition | DripEdition): edition is DripEdition {
    return edition.campaign.type === 'DRIP_SEQUENCE';
}
